import React from 'react';

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'purple';

interface StatusBadgeProps {
  status: string;
  label?: string;
  tone?: BadgeTone;
  className?: string;
}

const successStatuses = new Set(['active', 'approved', 'completed', 'confirmed', 'verified', 'success', 'serviceable']);
const warningStatuses = new Set(['pending', 'review', 'conflict', 'warning', 'due', 'unserviceable']);
const dangerStatuses = new Set(['inactive', 'rejected', 'revoked', 'lost', 'stolen', 'danger', 'unrecorded']);
const infoStatuses = new Set(['issued', 'generated', 'submitted', 'in_storage', 'returned', 'unread']);
const neutralStatuses = new Set(['read', 'dismissed']);

export const humanizeBadgeText = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const inferTone = (status: string): BadgeTone => {
  const normalized = status.toLowerCase();
  if (successStatuses.has(normalized)) return 'success';
  if (warningStatuses.has(normalized)) return 'warning';
  if (dangerStatuses.has(normalized)) return 'danger';
  if (infoStatuses.has(normalized)) return 'info';
  if (neutralStatuses.has(normalized)) return 'neutral';
  return 'neutral';
};

const joinClasses = (...classes: Array<string | undefined | false>) =>
  classes.filter(Boolean).join(' ');

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  tone,
  className,
}) => {
  const normalizedStatus = status.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const badgeTone = tone || inferTone(status);
  return (
    <span className={joinClasses('status-badge', `status-badge--${badgeTone}`, normalizedStatus && `status-badge--status-${normalizedStatus}`, className)}>
      {label || humanizeBadgeText(status)}
    </span>
  );
};

export default StatusBadge;
